import { captureMessage, withScope } from '@sentry/react';
import { isNil } from 'lodash';
import type { ReactNode } from 'react';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import { logoutSelf } from 'state/server/self';
import { parsePathForIdentifiers } from 'utils/error';

const push = (route: string | URL) => {
  if (typeof window === 'undefined') {
    // eslint-disable-next-line no-console -- will be remove in [sc-37500]
    console.error(`Could not redirect to ${route} as window was undefined`);
  }
  window.location.replace(route);
};

export const onErrorHandler = (err: any) => {
  const statusCode = err?.response?.status;

  if (
    isNil(statusCode) ||
    statusCode >= 500 ||
    statusCode === 0 ||
    err?.message === 'Network Error'
  ) {
    push(`/error`);
    return;
  }

  if (statusCode === 401) {
    logoutSelf(queryClient).catch((error: unknown) => {
      withScope(scope => {
        scope.setExtra('cause', error);
        captureMessage(
          'Something went wrong attempting to logoutSelf in the ReactQuery error handler.',
        );
      });
      push('/error');
    });
    return;
  }

  // captures all other 4xx error codes
  if (statusCode >= 400 && statusCode < 500) {
    const errorUrl = new URL(err.response?.request?.responseURL);

    const fingerprintPath = parsePathForIdentifiers(errorUrl.pathname);

    withScope(scope => {
      scope.setFingerprint([fingerprintPath, statusCode]);
      scope.setTag('path', errorUrl.pathname);
      scope.setExtra('response', err.response);
      captureMessage(
        `API Error: ${fingerprintPath} ${err.response.status} ${err.response.statusText}`,
        { level: 'error' },
      );
    });
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: onErrorHandler,
  }),
  defaultOptions: {
    queries: {
      retry: false,
      /** @todo [sc-37500] use push from useSummerHistory instead. */
    },
  },
});

export const ReactQueryClientProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
